import {
  Avatar,
  Button,
  FormField,
  List,
  ListItem,
  ListItemText,
} from '@/components/ui';
import { AUTHORITIES } from '@/config/constants';
import { useAppDispatch, useAppSelector } from '@/config/store';
import { hasAnyAuthority } from '@/shared/auth/private-route';
import { Form } from '@/shared/form';
import { IUser } from '@/shared/model/user.model';
import {
  AuthenticationState,
  clearAccounts,
  getAccounts,
  login,
} from '@/shared/reducers/authentication';
import { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Translate, translate } from 'react-jhipster';
import { Link, Navigate } from 'react-router-dom';
import { z } from 'zod';

function Copyright() {
  return (
    <p className="text-center text-caption text-[var(--color-mockup-text-mid)]">
      {'Copyright © '}
      <a
        href="https://www.alest.it/edocendo-2/"
        className="text-primary hover:underline font-heading font-semibold"
      >
        eDocendo
      </a>{' '}
      {new Date().getFullYear()}
      {'.'}
    </p>
  );
}

function RememberMeCheckbox() {
  const { control } = useFormContext();
  return (
    <Controller
      name="rememberMe"
      control={control}
      render={({ field }) => (
        <label className="mt-4 flex cursor-pointer items-center gap-2">
          <input
            type="checkbox"
            checked={!!field.value}
            onChange={(e) => field.onChange(e.target.checked)}
            onBlur={field.onBlur}
            className="h-4 w-4 rounded border-2 border-[var(--color-mockup-border)] text-primary focus:ring-primary"
          />
          <span className="text-sm text-[var(--color-mockup-text)]">
            <Translate contentKey="login.form.rememberme">Remember me</Translate>
          </span>
        </label>
      )}
    />
  );
}

const loginSchema = z.object({
  username: z.string().min(1, 'This field is required.'),
  password: z.string().min(1, 'This field is required.'),
  rememberMe: z.boolean().optional(),
});

type LoginFormValues = z.infer<typeof loginSchema>;

export const Login = () => {
  const dispatch = useAppDispatch();
  const { isAuthenticated, accounts, account } = useAppSelector<AuthenticationState>(
    (state) => state.authentication
  );
  const [credentials, setCredentials] = useState<LoginFormValues | null>(null);

  const handleSubmit = (data: LoginFormValues) => {
    setCredentials(data);
    dispatch(getAccounts({ username: data.username, password: data.password, rememberMe: data.rememberMe ?? false }));
  };

  const handleLogin = (id: number) => {
    if (!credentials) return;
    dispatch(login(id, credentials.password, credentials.rememberMe ?? false));
  };

  const getProfileRole = (user: IUser) => {
    if (user.roles.findIndex((role) => role.name === AUTHORITIES.ROLE_ADMIN) !== -1) return 'Amministratore';
    if (user.roles.findIndex((role) => role.name === AUTHORITIES.ROLE_VICE) !== -1) return 'Vicepreside';
    if (user.roles.findIndex((role) => role.name === AUTHORITIES.ROLE_TEACHER) !== -1) return 'Docente';
    if (user.roles.findIndex((role) => role.name === AUTHORITIES.ROLE_STUDENT) !== -1) return 'Studente';
    if (user.roles.findIndex((role) => role.name === AUTHORITIES.ROLE_PARENT) !== -1) return 'Genitore';
    return 'Utente';
  };

  const getProfilePrimary = (user: IUser) => {
    const profileRole = getProfileRole(user);
    if (user.firstName && user.lastName) return `${user.firstName} ${user.lastName} (${profileRole})`;
    if (user.firstName) return `${user.firstName} (${profileRole})`;
    if (user.lastName) return `${user.lastName} (${profileRole})`;
    return profileRole;
  };

  useEffect(() => {
    dispatch(clearAccounts());
  }, [dispatch]);

  if (isAuthenticated && account) {
    if (hasAnyAuthority(account.roles, [AUTHORITIES.ROLE_PARENT])) return <Navigate to="/parents" />;
    return <Navigate to="/" />;
  }

  if (accounts?.users?.length > 0 && credentials) {
    return (
      <div className="flex min-h-screen">
        <div
          className="hidden flex-1 bg-cover bg-center bg-no-repeat sm:block sm:w-4/12 md:w-7/12 bg-[url('/class.jpg')] bg-[var(--color-page)]"
        />
        <div className="flex w-full flex-col items-center justify-center rounded-3xl border-2 border-[var(--color-mockup-border)] bg-surface p-5 shadow-card sm:w-8/12 sm:p-6 md:w-5/12 md:p-7">
          <Avatar src="/package/logo.png" className="mb-2.5 h-[96px] w-[96px] border-0 bg-transparent sm:h-[104px] sm:w-[104px]" />
          <h1 className="text-heading-1 font-heading font-bold tracking-tight text-[var(--color-mockup-text)]">
            Seleziona un profilo
          </h1>
          <div className="mt-2.5 w-full md:w-[70%]">
            <List>
              {accounts.users.map((user) => (
                <ListItem key={user.id} className="list-none">
                  <button
                    type="button"
                    onClick={() => handleLogin(user.id)}
                    className="flex w-full items-center gap-3 rounded-2xl px-4 py-2.5 text-left transition-colors duration-200 hover:bg-primary/10 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
                  >
                    <Avatar
                      className="h-10 w-10 shrink-0"
                      fallback={`${user.firstName?.[0] ?? ""}${user.lastName?.[0] ?? ""}`}
                    />
                    <ListItemText
                      primary={getProfilePrimary(user)}
                      secondary={user.email}
                    />
                  </button>
                </ListItem>
              ))}
            </List>
            <div className="mt-2.5">
              <Copyright />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen">
      <div
        className="hidden flex-1 bg-cover bg-center bg-no-repeat sm:block sm:w-4/12 md:w-7/12 bg-[url('/class.jpg')] bg-[var(--color-page)]"
      />
      <div className="flex w-full flex-col items-center justify-center rounded-3xl border-2 border-[var(--color-mockup-border)] bg-surface p-5 shadow-card sm:w-8/12 sm:p-6 md:w-5/12 md:p-7">
        <Avatar src="/package/logo.png" className="mb-2.5 h-[96px] w-[96px] border-0 bg-transparent sm:h-[104px] sm:w-[104px]" />
        <h1 className="text-heading-1 font-heading font-bold tracking-tight text-[var(--color-mockup-text)]">
          <Translate contentKey="login.title">Sign in</Translate>
        </h1>
        <div className="mt-2.5 w-full md:w-[70%]">
          <Form schema={loginSchema} onSubmit={handleSubmit} defaultValues={{ username: '', password: '', rememberMe: false }}>
            <FormField name="username" label={translate('global.form.username.label')} autoComplete="username" autoFocus className="mt-3" />
            <FormField name="password" label={translate('login.form.password')} type="password" autoComplete="current-password" className="mt-3" />
            <RememberMeCheckbox />
            <Button type="submit" className="mt-3 w-full" disabled={accounts?.loading}>
              {accounts?.loading ? '...' : <Translate contentKey="login.form.button">Sign in</Translate>}
            </Button>
            <div className="mt-3">
              <Link
                to="/account/reset/request"
                className="text-body-sm text-primary transition-colors duration-200 hover:underline focus-visible:rounded-sm focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              >
                <Translate contentKey="login.password.forgot">Did you forget your password?</Translate>
              </Link>
            </div>
            <div className="mt-5">
              <Copyright />
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default Login;
